/**
 * Puzzle generation.
 *
 * The shading comes first, and everything else is read off it. That is the whole
 * trick of this genre: *any* shading of the grid is a legal answer. Colour the
 * cells however you like, take the connected blocks of one shade as the regions,
 * write each block's size in one of its cells, and the rules are satisfied by
 * construction — same-shade regions cannot touch, because two touching blocks of
 * one shade are one block.
 *
 * So a board is never searched for. It is built, and only two things are then
 * asked of it:
 *
 *   1. Is the answer the *only* answer? Numbers say how big a region is, not
 *      where it sits, and two small regions can often swap their shapes. This is
 *      where the attempts go — see `countSolutions`.
 *   2. What does solving it need? `grade` runs the technique ladder and reports
 *      the hardest rung the board forces, which is the difficulty the player was
 *      promised.
 *
 * Which cell of a region carries its number is the one free choice left after
 * the shading, and it decides both. A number in a bulge of a shape leaves that
 * shape as the only one that fits; a number in the middle of an open patch
 * leaves dozens. Each shading is therefore tried with several different sets of
 * number positions before it is thrown away, because the shading is the cheap
 * part and the uniqueness check is not.
 *
 * Two preferences shape which of the boards that pass gets handed over.
 *
 *   - **Symmetry.** The shading is grown so that a half turn of the board swaps
 *     every square's shade — see `growShading` — and every region then has a
 *     twin of the other shade and the same size. The twin's number goes on the
 *     square the half turn lands on, so the numbers the player sees are laid
 *     out in a half turn of themselves, and on expert the ?s are rubbed out in
 *     pairs to keep it. Exact symmetry is tried first on every shading; a
 *     single pair is allowed to come loose if that is what it takes to find a
 *     unique answer, and only once most of the budget is gone does the search
 *     give up on symmetric shadings altogether.
 *   - **Flow.** Among boards of the right tier, the one that leads best — where
 *     each step's consequence most often sits right beside it, so the next
 *     thing to see is uncovered by the last — as `flowOf` in solver.ts
 *     measures it. Several boards are built and weighed; see `POLISH_BOARDS`.
 *
 * Blocks are grown to a size that was rolled for them, but they are allowed to
 * merge where the grid leaves no alternative — a cell in the gap between two
 * blocks of one shade joins both — as long as the result still fits inside the
 * largest number allowed. That is why sizes are read back off the finished grid
 * rather than remembered from the intent: the intent is a request, and the grid
 * is what happened.
 */

import { type Geometry, type Region, geometry, regionsOf, turned } from './board';
import { type Placement, allPlacements, placementsFor } from './placements';
import { makeRng, randomSeed, type Rng } from './rng';
import { analyse, countSolutions, flowOf, grade } from './solver';
import {
  BOARDS,
  DIFFICULTIES,
  LIGHT,
  MAX_REGION,
  SHADOW,
  UNKNOWN,
  type Clue,
  type Difficulty,
  type Puzzle,
  type Shade,
  other,
} from './types';

export interface GenerateOptions {
  difficulty: Difficulty;
  seed?: number;
  /** Wall-clock budget. Generation returns its best board when this runs out. */
  budgetMs?: number;
  /** Called between attempts so a UI can paint. */
  onProgress?: (attempt: number) => Promise<void> | void;
}

/**
 * Search budget.
 *
 * The bottom three tiers are found in well under a second. Expert is the one
 * this is sized for: it grades every candidate twice and runs the ladder once
 * per number it tries to rub out, which is a few hundred milliseconds an attempt
 * here and several times that on a phone. Running out is not an error — the
 * search hands back the closest board it found, and only an attempt that never
 * finished at all leaves it with nothing.
 */
const BUDGET_MS = 12000;

/**
 * How many sets of number positions one shading is worth.
 *
 * Re-rolling the numbers costs a uniqueness check; re-rolling the shading costs
 * that plus a fresh grid, and throws away a layout that may well be one number
 * away from working.
 */
const CLUE_TRIES = 8;

/**
 * How often a region of each size is asked for, by index, per tier.
 *
 * This is the other half of the difficulty, and the half a player feels first.
 * Region size and region *count* are the same dial turned from opposite ends —
 * a grid is a fixed number of cells, so asking for bigger blocks is asking for
 * fewer of them — and both ends of that dial make a board harder. A small block
 * is a handhold: a 1 settles its four neighbours the moment it is seen, and a 2
 * has four shapes at most. A 6 has hundreds, so it cannot be read off the board
 * and has to be squeezed by what is around it; and with fewer numbers there is
 * less around it doing the squeezing.
 *
 * So the bottom tiers keep the old spread, weighted at threes and fours, which
 * lands the finished board near four. The top two drop ones entirely and shift
 * up one, which merges then carry to about five. Those two share a spread and
 * differ by `MIN_REGION` and by the board under them, which is enough: hard
 * falls from fourteen blocks to twelve and expert from sixteen to thirteen,
 * and both gain about a square per block.
 *
 * Pushing further than this is where it stops paying. Weights centred on six
 * will build a grid — around ten blocks of six on the expert board — but a third
 * of those grids have no *opening*: with that few numbers on 64 squares, nothing
 * follows from any of them, and the ladder in `solver.ts` stalls around twenty
 * squares in with no contradiction and no agreement anywhere on the board. A
 * board nobody can start is not a hard board.
 */
export const SIZE_WEIGHTS: Record<Difficulty, readonly number[]> = {
  easy: [0, 2, 5, 6, 5, 3, 2, 1],
  medium: [0, 2, 5, 6, 5, 3, 2, 1],
  hard: [0, 0, 1, 4, 6, 5, 3, 0],
  expert: [0, 0, 1, 4, 6, 5, 3, 0],
};

/**
 * The smallest block a tier will accept, and the other half of the same dial.
 *
 * Weights alone cannot deliver a board of big blocks, because they are a request
 * and the grid is what happens: a block that walls in a single square leaves a
 * square that can only ever be a 1, and no weighting of the roll prevents it.
 * Asking for sixes on the two big grids still lands one block in seven at three
 * or under, and those are the squares a solver goes to first — a 1 settles its
 * four neighbours on sight, a 2 has four shapes at most. One of them on an
 * expert board is a free foothold in the middle of the reasoning it is supposed
 * to demand.
 *
 * So the floor is checked against the finished grid rather than aimed at, and a
 * shading that misses it is thrown away. That costs dozens of re-rolls per
 * accepted grid at the expert floor, which sounds worse than it is: `growShading`
 * is a fraction of a millisecond and everything downstream of it — the
 * uniqueness check, the ladder, the hiding — is not. This is much the cheaper
 * end to be strict at.
 */
export const MIN_REGION: Record<Difficulty, number> = {
  easy: 1,
  medium: 1,
  hard: 2,
  expert: 3,
};

function pickSize(rng: Rng, weights: readonly number[]): number {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let roll = rng.int(total);
  for (let size = 1; size < weights.length; size += 1) {
    roll -= weights[size] as number;
    if (roll < 0) return size;
  }
  return MAX_REGION;
}

/**
 * The block a cell would belong to if it took this shade.
 *
 * Everything about growth is decided with this, because the cost of shading a
 * cell is never the cell: it is every block that cell would weld together. The
 * gaps left between finished blocks are exactly the cells with blocks on more
 * than one side, so a generator that counts one of them counts wrong.
 */
function blockWith(
  geo: Geometry,
  shades: readonly number[],
  cell: number,
  shade: number,
): number[] {
  const seen = new Set<number>([cell]);
  const stack = [cell];
  while (stack.length > 0) {
    const next = stack.pop() as number;
    for (const neighbor of geo.neighbors[next] as number[]) {
      if (seen.has(neighbor) || shades[neighbor] !== shade) continue;
      seen.add(neighbor);
      stack.push(neighbor);
    }
  }
  return [...seen];
}

/**
 * The next cell to grow a block from.
 *
 * The most hemmed-in undecided cell, so gaps are filled while they are still
 * gaps. Seeding at random instead leaves single cells stranded between finished
 * blocks, and a stranded cell has no choice but to join one of them — which is
 * how a five turns into a seven.
 */
function nextSeed(geo: Geometry, shades: readonly number[], rng: Rng): number {
  let best: number[] = [];
  let fewest = Number.POSITIVE_INFINITY;
  for (let cell = 0; cell < geo.cells; cell += 1) {
    if (shades[cell] !== UNKNOWN) continue;
    const room = free(geo, shades, cell);
    if (room < fewest) {
      fewest = room;
      best = [cell];
    } else if (room === fewest) {
      best.push(cell);
    }
  }
  return rng.pick(best);
}

/** How many of a cell's neighbours are still undecided. */
function free(geo: Geometry, shades: readonly number[], cell: number): number {
  return (geo.neighbors[cell] as number[]).filter((neighbor) => shades[neighbor] === UNKNOWN)
    .length;
}

/**
 * Cells this one would wall in that the block could not then reach.
 *
 * A block growing past a cell is what leaves a hole, and a hole is a block of
 * one — the worst square on a hard board, since a 1 settles its four neighbours
 * the moment it is seen. The block cannot always avoid it, but it can nearly
 * always take a different cell first, and the choice costs one lookahead: a
 * neighbour whose *only* undecided neighbour is this cell is a neighbour that
 * closes when this cell is taken, and it is lost if the block would then be over
 * the ceiling for it.
 */
function wallsIn(
  geo: Geometry,
  shades: readonly number[],
  option: { cell: number; block: number[] },
): number {
  if (option.block.length < MAX_REGION) return 0;
  let count = 0;
  for (const neighbor of geo.neighbors[option.cell] as number[]) {
    if (shades[neighbor] !== UNKNOWN) continue;
    if (free(geo, shades, neighbor) === 1) count += 1;
  }
  return count;
}

/** The undecided cells a block could take next, with the block each would make. */
function frontier(
  geo: Geometry,
  shades: readonly number[],
  block: readonly number[],
  shade: number,
): { cell: number; block: number[] }[] {
  const seen = new Set<number>();
  const out: { cell: number; block: number[] }[] = [];
  for (const cell of block) {
    for (const neighbor of geo.neighbors[cell] as number[]) {
      if (shades[neighbor] !== UNKNOWN || seen.has(neighbor)) continue;
      seen.add(neighbor);
      out.push({ cell: neighbor, block: blockWith(geo, shades, neighbor, shade) });
    }
  }
  return out;
}

/**
 * A shading of the whole grid: the answer, before it is a puzzle.
 *
 * One block at a time, and no cell is ever shaded if doing so would make a block
 * larger than the biggest number allowed. That rule is enforced against the
 * merged size — the block the cell would create, not the one it is growing —
 * which is what keeps the sizes on the finished grid the ones that were asked
 * for. Blocks may still merge, and often do where two of them close around a
 * gap; the cell simply is not taken unless the result still fits.
 *
 * The other thing a block does before it stops is sweep up after itself. A cell
 * left with no undecided neighbour has nowhere to go: it is not a block of one,
 * it is a weld between whatever has closed around it, and the size it makes is
 * the sum of them plus one. Absorbing it now, while this block still has room
 * for it, is the difference between a grid of the sizes that were asked for and
 * a grid that gives up — and giving up gets common fast as the weights climb,
 * because a weld between two blocks already near the ceiling fits nowhere at
 * all. That is also why sweeping ignores the target: a cell that has to be taken
 * has to be taken, and the ceiling is the only limit that means anything here.
 *
 * Null when a gap has run out of room on both sides anyway: a cell hemmed in by
 * a full block of each shade cannot be shaded at all, and the grid has to be
 * rebuilt. Cheaper to abandon than to unpick.
 */
export function growShading(
  geo: Geometry,
  rng: Rng,
  weights: readonly number[],
  symmetric = false,
): number[] | null {
  const shades = new Array<number>(geo.cells).fill(UNKNOWN);
  let left = geo.cells;
  // Symmetric growth paints every cell twice: the cell, and the one a half turn
  // puts it on in the other shade. The grid is then the same board turned over
  // at every moment of its growth, so every block has a twin of the same shape
  // and the other shade, and nothing below needs to know — a check that passes
  // for a cell passes for its twin, for the same reason.
  const paint = (cell: number, shade: number): void => {
    shades[cell] = shade;
    left -= 1;
    if (!symmetric) return;
    shades[turned(geo, cell)] = other(shade as Shade);
    left -= 1;
  };

  while (left > 0) {
    const seed = nextSeed(geo, shades, rng);
    const choices = rng
      .shuffle([LIGHT, SHADOW])
      .map((shade) => ({ shade, block: blockWith(geo, shades, seed, shade) }));
    choices.sort((a, b) => a.block.length - b.block.length);
    const start = choices[0] as { shade: number; block: number[] };
    if (start.block.length > MAX_REGION) return null;

    const shade = start.shade;
    // Never below what the seed already joins: a cell dropped into a gap beside
    // a four is a five whatever size was rolled for it.
    const target = Math.max(start.block.length, Math.min(pickSize(rng, weights), MAX_REGION));
    paint(seed, shade);
    let block = start.block;

    for (;;) {
      const options = frontier(geo, shades, block, shade);
      const stranded = options.filter(
        (option) => option.block.length <= MAX_REGION && free(geo, shades, option.cell) === 0,
      );
      const room = options.filter((option) => option.block.length <= target);
      let taken: { cell: number; block: number[] } | null = null;
      if (stranded.length > 0) {
        // Whichever weld is smallest: the point is to spend as little of the
        // ceiling as this cell costs, not to grow.
        const cheapest = Math.min(...stranded.map((option) => option.block.length));
        taken = rng.pick(stranded.filter((option) => option.block.length === cheapest));
      } else if (block.length < target && room.length > 0) {
        // Cells that would wall something in are a last resort; after that, the
        // most hemmed-in cell on offer, for the reason `nextSeed` picks one: a
        // cell left behind while the block grows past it is a cell that will
        // later have to join whatever closed around it.
        const safest = Math.min(...room.map((option) => wallsIn(geo, shades, option)));
        const open = room.filter((option) => wallsIn(geo, shades, option) === safest);
        const tightest = Math.min(...open.map((option) => free(geo, shades, option.cell)));
        taken = rng.pick(open.filter((option) => free(geo, shades, option.cell) === tightest));
      }
      if (!taken) break;
      paint(taken.cell, shade);
      block = taken.block;
    }
  }

  return shades;
}

/**
 * How many grids one attempt will roll looking for the tier's floor.
 *
 * Around fifty are needed at the expert floor, so this is deep enough that
 * missing is rare and shallow enough to be worth much less than the uniqueness
 * check it protects: fifty grids cost about what one of those does.
 */
const SHADING_TRIES = 400;

/** A shading with nothing on it under the tier's floor. */
function shadingFor(
  geo: Geometry,
  rng: Rng,
  difficulty: Difficulty,
  deadline: number,
  symmetric = false,
): number[] | null {
  const weights = SIZE_WEIGHTS[difficulty] as readonly number[];
  const floor = MIN_REGION[difficulty];
  for (let tries = 0; tries < SHADING_TRIES; tries += 1) {
    if ((tries & 0x1f) === 0 && Date.now() > deadline) return null;
    const shading = growShading(geo, rng, weights, symmetric);
    if (!shading) continue;
    if (regionsOf(geo, shading).every((region) => region.cells.length >= floor)) return shading;
  }
  return null;
}

/**
 * The tiers that rub numbers out, and how many of a board's they may take.
 *
 * A `?` says only *there is a block here, and this square is in it* — the size
 * is the player's to work out from everything else. That is a different kind of
 * hard from a big region rather than more of the same, which is why it is what
 * separates the top tier: expert is not "hard with more of it", it is a board
 * where some of the numbers have to be deduced before they can be used.
 *
 * A third of them, at most. Every number rubbed out is one the *rest* of the
 * board has to pin down, and past about a third there is not enough left to do
 * the pinning: the uniqueness check simply starts refusing, and the ones that
 * survive it tend to be boards with no opening — see `grade`, which drops them.
 */
const HIDDEN_SHARE: Partial<Record<Difficulty, number>> = { expert: 1 / 3 };

/**
 * Rubs out as many numbers as the board can lose and still be the only board.
 *
 * Taken one at a time and checked each time, because they are not independent:
 * two numbers can each be deducible on their own and not both at once. The order
 * is shuffled so it is not always the top-left ones that go.
 *
 * The test each candidate has to pass is that the ladder still finishes the
 * board, and that one test covers both things a board has to be. Every rung only
 * ever draws a conclusion that *follows*, so a board the ladder fills in
 * completely has no second answer to find — any solution has to agree with every
 * forced square, and there are no others left. Solvable is the strictly stronger
 * question here, and it is the cheaper one to ask.
 *
 * It is also the one that matters. A board keeps its single answer long past the
 * point where anyone can *find* it: rub out a third of the numbers and the
 * search will usually still say "one" while the ladder stalls a dozen squares
 * in. Checking only uniqueness would have made this tier a guessing game with a
 * `?` drawn on it.
 *
 * Only the hidden number's own shapes change, which is what makes this cheap
 * enough to do inside the generation loop. What a block may not contain is
 * decided by where the *other* numbers sit and what shade they are, and rubbing
 * a value out moves none of them — so one list is rebuilt and the rest are handed
 * over exactly as they were.
 */
function hideNumbers(
  geo: Geometry,
  puzzle: Puzzle,
  share: number,
  rng: Rng,
  deadline: number,
): Clue[] {
  const clues = puzzle.clues.slice();
  const lists = allPlacements(geo, clues);
  const most = Math.floor(clues.length * share);
  // A number and its twin across the half turn go together, so a symmetric
  // board keeps its symmetry with the ?s drawn on it: the pattern the player
  // sees is the numbers' positions *and* which of them are missing.
  const at = new Map(clues.map((clue, index) => [clue.cell, index]));
  const groups: number[][] = [];
  const grouped = new Set<number>();
  for (const index of rng.shuffle(clues.map((_, position) => position))) {
    if (grouped.has(index)) continue;
    const twin = at.get(turned(geo, (clues[index] as Clue).cell));
    const group = twin !== undefined && twin !== index ? [index, twin] : [index];
    for (const member of group) grouped.add(member);
    groups.push(group);
  }
  let hidden = 0;

  for (const group of groups) {
    if (hidden + group.length > most || Date.now() > deadline) continue;
    const candidate = clues.slice();
    const swapped: Placement[][] = lists.slice();
    for (const index of group) candidate[index] = { ...(clues[index] as Clue), value: null };
    for (const index of group) swapped[index] = placementsFor(geo, candidate, index);

    if (!analyse({ ...puzzle, clues: candidate }, deadline, swapped).solved) continue;
    for (const index of group) {
      clues[index] = candidate[index] as Clue;
      lists[index] = swapped[index] as Placement[];
    }
    hidden += group.length;
  }
  return clues;
}

/**
 * How close a board's numbers come to a half turn of themselves, from 0 to 1.
 *
 * The share of numbers whose twin square — the one a half turn lands on — holds
 * a number too, shown or hidden alike. A symmetric board is 1; one that had a
 * pair loosened to find a unique answer is a little under.
 */
export function symmetryOf(puzzle: Puzzle): number {
  const geo = geometry(puzzle.width, puzzle.height);
  const at = new Map(puzzle.clues.map((clue) => [clue.cell, clue]));
  let matched = 0;
  for (const clue of puzzle.clues) {
    const twin = at.get(turned(geo, clue.cell));
    if (twin && (twin.value === null) === (clue.value === null)) matched += 1;
  }
  return puzzle.clues.length === 0 ? 1 : matched / puzzle.clues.length;
}

/**
 * Where each region's number goes, and whether the board keeps its half turn.
 *
 * On a symmetric shading every region has a twin — see `growShading` — so a
 * number is placed in one region at random and its twin's goes on the square
 * the half turn puts it on. `loose` is how many pairs may break that and place
 * their second number anywhere in its region instead: symmetric boards are a
 * little less often unique, and one loosened pair is usually enough to find an
 * answer on a shading that is otherwise worth keeping.
 */
export function placeClues(
  geo: Geometry,
  regions: readonly Region[],
  rng: Rng,
  symmetric: boolean,
  loose: number,
): Clue[] {
  const clueIn = (region: Region, cell: number): Clue => ({
    cell,
    value: region.cells.length,
    shade: region.shade as Shade,
  });
  if (!symmetric) {
    return regions
      .map((region) => clueIn(region, rng.pick(region.cells)))
      .sort((a, b) => a.cell - b.cell);
  }

  const regionOf = new Int32Array(geo.cells);
  regions.forEach((region, index) => {
    for (const cell of region.cells) regionOf[cell] = index;
  });
  const pairs: [number, number][] = [];
  const paired = new Set<number>();
  regions.forEach((region, index) => {
    if (paired.has(index)) return;
    const twin = regionOf[turned(geo, region.cells[0] as number)] as number;
    paired.add(index);
    paired.add(twin);
    pairs.push([index, twin]);
  });
  const loosened = new Set(rng.shuffle(pairs.map((_, index) => index)).slice(0, loose));

  const clues: Clue[] = [];
  pairs.forEach(([first, second], index) => {
    const region = regions[first] as Region;
    const twin = regions[second] as Region;
    const cell = rng.pick(region.cells);
    clues.push(clueIn(region, cell));
    if (second === first) return;
    clues.push(clueIn(twin, loosened.has(index) ? rng.pick(twin.cells) : turned(geo, cell)));
  });
  return clues.sort((a, b) => a.cell - b.cell);
}

/**
 * How many of a symmetric shading's number sets are tried fully symmetric
 * before a pair is allowed to come loose.
 *
 * Half. Exact symmetry is what the board is for, so it gets the first go on
 * every shading; the loosened tries are there so that a shading one number away
 * from unique is not thrown out for want of that number.
 */
const EXACT_TRIES = CLUE_TRIES / 2;

/**
 * How long the uniqueness search gets before the ladder is asked instead.
 *
 * See `attemptOnce`. A fraction of what the slow searches took — the worst ran
 * for seconds on a symmetric expert board — and several times what the typical
 * one does.
 */
const SEARCH_MS = 60;

/** One shading, tried with several sets of number positions. */
function attemptOnce(
  geo: Geometry,
  rng: Rng,
  difficulty: Difficulty,
  deadline: number,
  symmetric: boolean,
): Puzzle | null {
  const attemptSeed = rng.int(0x7fffffff);
  const local = makeRng(attemptSeed);
  const shading = shadingFor(geo, local, difficulty, deadline, symmetric);
  if (!shading) return null;
  const regions = regionsOf(geo, shading);

  const base = {
    width: geo.width,
    height: geo.height,
    solution: shading as Shade[],
    seed: attemptSeed,
  };

  for (let attempt = 0; attempt < CLUE_TRIES; attempt += 1) {
    if (Date.now() > deadline) return null;
    const loose = attempt < EXACT_TRIES ? 0 : 1;
    const clues = placeClues(geo, regions, local, symmetric, loose);

    const puzzle: Puzzle = { ...base, clues, difficulty: 'expert' };
    // The search is the quick way to throw out a board with two answers, which
    // is most of them — a second answer usually turns up in a few hundred
    // nodes. Proving there is *no* second one can take far longer on a big
    // board, though, and does not have to be done here: the ladder below only
    // draws conclusions that follow, so a board it finishes is a board with one
    // answer. A search that runs long is left to it.
    const search = countSolutions(puzzle, 2, Math.min(deadline, Date.now() + SEARCH_MS));
    if (Date.now() > deadline) return null;
    if (search.count > 1 || (search.complete && search.count !== 1)) continue;

    // Graded before anything is rubbed out. The ladder failing here is these
    // number positions' doing rather than the hiding's, and finding that out now
    // costs one pass where finding it out afterwards costs a dozen — hiding runs
    // the ladder once per number it tries. Another set of positions on the same
    // shading often does have an opening, which is what the loop goes back for.
    const opening = grade(puzzle, deadline);
    if (!opening) continue;

    const share = HIDDEN_SHARE[difficulty];
    if (!share) return { ...puzzle, difficulty: opening };

    // Rubbing numbers out only ever makes a board harder, and it happens before
    // the grading that counts: what the player is handed is what gets graded.
    const shown: Puzzle = { ...puzzle, clues: hideNumbers(geo, puzzle, share, local, deadline) };
    const earned = grade(shown, deadline);
    if (!earned) continue;
    return { ...shown, difficulty: earned };
  }
  return null;
}

function distance(from: Difficulty, to: Difficulty): number {
  return Math.abs(DIFFICULTIES.indexOf(from) - DIFFICULTIES.indexOf(to));
}

/**
 * How many boards of the right tier to weigh up before handing one over.
 *
 * The first board that fits is a puzzle; the point of looking further is to
 * hand over the best of a few, ranked by symmetry and then by `flowOf`. A count
 * rather than a stretch of time, so that a seed still names one board however
 * fast the device is; sized to what a board costs at each tier — tens of
 * milliseconds at the bottom, a few hundred on expert.
 */
const POLISH_BOARDS: Record<Difficulty, number> = { easy: 8, medium: 8, hard: 6, expert: 3 };
/**
 * The longest the weighing-up may add, whatever the count says.
 *
 * Only a slow device ever reaches this; it is there so that one never waits
 * seconds for a nicer board when it already has a good one.
 */
const POLISH_MS = 2500;
/**
 * The share of the budget spent insisting on a symmetric shading.
 *
 * Most of it. Symmetric shadings are a little less often unique, but they are
 * what a player notices first about a board that was made rather than rolled,
 * and the whole of the easier tiers' search takes a fraction of this. What is
 * left goes to the unconstrained generator, so that a tier which cannot find a
 * symmetric board in time still finds a board.
 */
const SYMMETRIC_SHARE = 0.7;

/**
 * How a board ranks against the others that fit the tier.
 *
 * Symmetry counts double. It is what makes a board look composed at a glance,
 * before a single square is shaded; flow is what makes it feel composed while
 * it is played, and among boards that are equally symmetric it decides.
 */
function merit(puzzle: Puzzle, deadline: number): number {
  const flow = flowOf(puzzle, deadline);
  return 2 * symmetryOf(puzzle) + (flow ? flow.score : 0);
}

export async function generatePuzzle(options: GenerateOptions): Promise<Puzzle> {
  const { difficulty } = options;
  const rng = makeRng(options.seed ?? randomSeed());
  const started = Date.now();
  const budget = options.budgetMs ?? BUDGET_MS;
  const deadline = started + budget;
  const symmetricUntil = started + budget * SYMMETRIC_SHARE;
  const board = BOARDS[difficulty];
  const geo = geometry(board.width, board.height);

  let best: Puzzle | null = null;
  let attempt = 0;
  let pick: { puzzle: Puzzle; merit: number } | null = null;
  let polishUntil = deadline;
  let weighed = 0;

  while (Date.now() < deadline) {
    if (pick && (Date.now() > polishUntil || weighed >= POLISH_BOARDS[difficulty])) break;
    attempt += 1;
    if (options.onProgress) await options.onProgress(attempt);
    const symmetric = Date.now() < symmetricUntil;
    const candidate = attemptOnce(geo, rng, difficulty, deadline, symmetric);
    if (!candidate) continue;
    if (candidate.difficulty === difficulty) {
      const worth = merit(candidate, deadline);
      weighed += 1;
      if (!pick) polishUntil = Math.min(deadline, Date.now() + POLISH_MS);
      if (!pick || worth > pick.merit) pick = { puzzle: candidate, merit: worth };
      continue;
    }
    if (
      !best ||
      distance(candidate.difficulty, difficulty) < distance(best.difficulty, difficulty)
    ) {
      best = candidate;
    }
  }

  if (pick) return pick.puzzle;
  if (best) return best;
  throw new Error('could not build a puzzle in time');
}
